import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import api from '@/lib/api';
import { getPctColor } from '@/lib/statColors';
import { computePPI, formatPPI } from '@/lib/ppi';
import { toast } from 'sonner';
import {
  Download,
  Lock,
  CheckCircle,
  Trash2,
  RefreshCw,
  Video,
  ExternalLink,
  X,
  Save,
  Edit,
  ClipboardList,
  FileSpreadsheet,
  History,
  ChevronDown,
  ChevronUp,
  Pencil,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { calcFourFactors, generateTeamAnalysis } from '@/lib/teamAnalysis';
import GameBreakdownModal from '@/components/GameBreakdownModal';

/* ── Design tokens (matching CoachLayout) ── */
const C = {
  orange: '#F5A623', navy: '#0D1B26', navyCard: '#1A2B3C',
  green: '#27AE60', red: '#E74C3C', blue: '#2980B9',
  muted: '#8FA3B1', textSec: '#A8BFCE', border: '#DDE5EC',
};

export default function GameSummary() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [boxScores, setBoxScores] = useState(null);
  const [playByPlay, setPlayByPlay] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [sideFilter, setSideFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Replay URL state
  const [replayUrl, setReplayUrl] = useState('');
  const [showReplayModal, setShowReplayModal] = useState(false);
  const [replayUrlInput, setReplayUrlInput] = useState('');
  const [savingReplayUrl, setSavingReplayUrl] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingEdits, setSavingEdits] = useState(false);

  // Edit history state
  const [editHistory, setEditHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Adjacent games for prev/next navigation
  const [adjGames, setAdjGames] = useState({ prev: null, next: null });

  useEffect(() => {
    fetchData();
    api.get(`/games/${gameId}/adjacent`).then(r => setAdjGames(r.data)).catch(() => {});
  }, [gameId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [boxRes, pbpRes] = await Promise.all([
        api.get(`/games/${gameId}/box-scores`),
        api.get(`/games/${gameId}/play-by-play`)
      ]);
      setBoxScores(boxRes.data);
      setPlayByPlay(pbpRes.data);
      const gameReplayUrl = boxRes.data?.game?.replay_url || '';
      setReplayUrl(gameReplayUrl);
      setReplayUrlInput(gameReplayUrl);
    } catch (error) {
      toast.error('Failed to load game summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchEditHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get(`/games/${gameId}/edit-history`);
      setEditHistory(res.data);
    } catch {
      toast.error('Failed to load edit history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && editHistory.length === 0) {
      fetchEditHistory();
    }
    setShowHistory(!showHistory);
  };

  const validateUrl = (url) => {
    if (!url.trim()) return true;
    return url.trim().startsWith('http://') || url.trim().startsWith('https://');
  };

  const handleSaveReplayUrl = async () => {
    const url = replayUrlInput.trim();
    if (url && !validateUrl(url)) {
      toast.error('Invalid URL. Must start with http:// or https://');
      return;
    }
    setSavingReplayUrl(true);
    try {
      await api.put(`/games/${gameId}/replay-url`, { replay_url: url || null });
      setReplayUrl(url);
      setShowReplayModal(false);
      toast.success(url ? 'Replay URL saved' : 'Replay URL cleared');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save replay URL');
    } finally {
      setSavingReplayUrl(false);
    }
  };

  const handleClearReplayUrl = async () => {
    setSavingReplayUrl(true);
    try {
      await api.put(`/games/${gameId}/replay-url`, { replay_url: null });
      setReplayUrl('');
      setReplayUrlInput('');
      setShowReplayModal(false);
      toast.success('Replay URL cleared');
    } catch (error) {
      toast.error('Failed to clear replay URL');
    } finally {
      setSavingReplayUrl(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      setDeleteConfirm(null);
      await api.post(`/games/${gameId}/recalculate-box-scores`);
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // === Inline Editing Functions ===
  const startEditing = useCallback(() => {
    if (!boxScores) return;
    const data = {};
    ['home', 'away'].forEach(side => {
      boxScores[side].players.forEach(p => {
        data[p.box_score_id] = {
          pts: p.pts || 0, fgm: p.fgm || 0, fga: p.fga || 0,
          three_pm: p.three_pm || 0, three_pa: p.three_pa || 0,
          ftm: p.ftm || 0, fta: p.fta || 0,
          oreb: p.oreb || 0, dreb: p.dreb || 0,
          ast: p.ast || 0, stl: p.stl || 0, blk: p.blk || 0,
          to: p.to || 0, pf: p.pf || 0
        };
      });
      // Also include team totals for editing
      if (boxScores[side].totals?.box_score_id) {
        const t = boxScores[side].totals;
        data[t.box_score_id] = {
          pts: t.pts || 0, fgm: t.fgm || 0, fga: t.fga || 0,
          three_pm: t.three_pm || 0, three_pa: t.three_pa || 0,
          ftm: t.ftm || 0, fta: t.fta || 0,
          oreb: t.oreb || 0, dreb: t.dreb || 0,
          ast: t.ast || 0, stl: t.stl || 0, blk: t.blk || 0,
          to: t.to || 0, pf: t.pf || 0
        };
      } else {
        // Create a placeholder for missing team totals (e.g., opponent in CSV imports)
        const placeholderId = `_new_totals_${side}`;
        data[placeholderId] = {
          pts: 0, fgm: 0, fga: 0, three_pm: 0, three_pa: 0,
          ftm: 0, fta: 0, oreb: 0, dreb: 0,
          ast: 0, stl: 0, blk: 0, to: 0, pf: 0
        };
      }
    });
    setEditData(data);
    setIsEditing(true);
  }, [boxScores]);

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
  };

  const handleEditChange = (boxScoreId, field, value) => {
    // Store raw string to allow multi-digit typing; convert on save
    setEditData(prev => ({
      ...prev,
      [boxScoreId]: { ...prev[boxScoreId], [field]: value }
    }));
  };

  const getEditNum = (bsId, field) => {
    const v = editData[bsId]?.[field];
    if (v === '' || v === undefined || v === null) return 0;
    return parseInt(v) || 0;
  };

  const saveEdits = async () => {
    setSavingEdits(true);
    try {
      const updates = [];
      const newTotals = [];
      
      Object.entries(editData).forEach(([bsId, fields]) => {
        // Convert all values to integers for the API
        const numFields = {};
        for (const [k, v] of Object.entries(fields)) {
          numFields[k] = parseInt(v) || 0;
        }
        const entry = {
          box_score_id: bsId,
          ...numFields,
          reb: (numFields.oreb || 0) + (numFields.dreb || 0)
        };
        if (bsId.startsWith('_new_totals_')) {
          const side = bsId.replace('_new_totals_', '');
          // Only send if any values are non-zero
          const hasData = Object.values(fields).some(v => v > 0);
          if (hasData) {
            newTotals.push({ side, ...entry });
          }
        } else {
          updates.push(entry);
        }
      });
      
      await api.put(`/games/${gameId}/box-scores`, { box_scores: updates, new_totals: newTotals });
      toast.success('Box scores updated');
      setIsEditing(false);
      setEditData({});
      setEditHistory([]);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingEdits(false);
    }
  };

  const exportBoxScoreCSV = () => {
    if (!boxScores) return;
    // CSV export uses the same "attempts === 0 → '-'" rule as the
    // rendered table so the exported file stays consistent with the UI.
    // PTS is derived from FGM/3PM/FTM (never read from a stored field)
    // for the same reason — the export must reflect the canonical math,
    // not a potentially-stale cached value.
    const ptsCsv = (r) => ((r.fgm || 0) - (r.three_pm || 0)) * 2 + (r.three_pm || 0) * 3 + (r.ftm || 0);
    const pctCsv = (made, attempts) => {
      if (!attempts || attempts <= 0) return '-';
      return Math.round((made / attempts * 100) * 10) / 10;
    };
    const headers = ['Player', 'PTS', 'FGM-FGA', 'FG%', '3PM-3PA', '3P%', 'FTM-FTA', 'FT%', 'OREB', 'DREB', 'REB', 'AST', 'STL', 'BLK', 'TO', 'PF'];
    let csv = headers.join(',') + '\n';
    csv += `\n${boxScores.home_team_name}\n`;
    boxScores.home.players.forEach(p => {
      csv += [
        `#${p.jersey_number || '?'} ${p.player_name}`,
        ptsCsv(p), `${p.fgm}-${p.fga}`, pctCsv(p.fgm, p.fga),
        `${p.three_pm}-${p.three_pa}`, pctCsv(p.three_pm, p.three_pa),
        `${p.ftm}-${p.fta}`, pctCsv(p.ftm, p.fta),
        p.oreb, p.dreb, p.reb, p.ast, p.stl, p.blk, p.to, p.pf
      ].join(',') + '\n';
    });
    if (boxScores.home.totals) {
      const t = boxScores.home.totals;
      csv += ['TEAM TOTALS', ptsCsv(t), `${t.fgm}-${t.fga}`, pctCsv(t.fgm, t.fga),
        `${t.three_pm}-${t.three_pa}`, pctCsv(t.three_pm, t.three_pa),
        `${t.ftm}-${t.fta}`, pctCsv(t.ftm, t.fta),
        t.oreb, t.dreb, t.reb, t.ast, t.stl, t.blk, t.to, t.pf
      ].join(',') + '\n';
    }
    csv += `\n${boxScores.away_team_name}\n`;
    boxScores.away.players.forEach(p => {
      csv += [
        `#${p.jersey_number || '?'} ${p.player_name}`,
        ptsCsv(p), `${p.fgm}-${p.fga}`, pctCsv(p.fgm, p.fga),
        `${p.three_pm}-${p.three_pa}`, pctCsv(p.three_pm, p.three_pa),
        `${p.ftm}-${p.fta}`, pctCsv(p.ftm, p.fta),
        p.oreb, p.dreb, p.reb, p.ast, p.stl, p.blk, p.to, p.pf
      ].join(',') + '\n';
    });
    if (boxScores.away.totals) {
      const t = boxScores.away.totals;
      csv += ['TEAM TOTALS', ptsCsv(t), `${t.fgm}-${t.fga}`, pctCsv(t.fgm, t.fga),
        `${t.three_pm}-${t.three_pa}`, pctCsv(t.three_pm, t.three_pa),
        `${t.ftm}-${t.fta}`, pctCsv(t.ftm, t.fta),
        t.oreb, t.dreb, t.reb, t.ast, t.stl, t.blk, t.to, t.pf
      ].join(',') + '\n';
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${boxScores.home_team_name}_vs_${boxScores.away_team_name}_BoxScore.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPlayByPlayCSV = () => {
    const headers = ['Period', 'Time', 'Team', 'Player', 'Action', 'Result'];
    let csv = headers.join(',') + '\n';
    playByPlay.forEach(e => {
      csv += [
        `Q${e.period}`, e.game_clock_time, e.side?.toUpperCase(),
        e.player_name ? `#${e.jersey_number || '?'} ${e.player_name}` : 'Team',
        e.event_type?.replace('_', ' ').toUpperCase(),
        e.points > 0 ? `+${e.points}` : (e.sub_type || '-')
      ].join(',') + '\n';
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${boxScores?.home_team_name || 'Game'}_PlayByPlay.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredPlayByPlay = playByPlay.filter(e => {
    if (periodFilter !== 'all' && e.period !== parseInt(periodFilter)) return false;
    if (sideFilter !== 'all' && e.side !== sideFilter) return false;
    return true;
  });

  // Shooting-percentage formatter. Color decisions are in
  // /app/frontend/src/lib/statColors.js (`getPctColor`) so all three
  // surfaces (GameSummary, PlayerStats, PlayerProfile) share one tier
  // ruleset tuned for youth basketball. We keep `formatPct` local
  // because it owns the "-" / decimal-place display rule which is
  // specific to the per-game box-score table.
  const formatPct = (pct, attempts) => {
    if (attempts === 0 || attempts == null) return '-';
    if (pct === null || pct === undefined) return '-';
    return pct.toFixed(1);
  };

  // ── Derived stats (build 208) ──────────────────────────────────────────
  // PTS and shooting percentages are NEVER read from stored fields when
  // rendering. They are always derived from the underlying shot counts
  // (FGM, 3PM, FTM, FGA, 3PA, FTA) so the displayed totals can never
  // fall out of sync with what the user has typed.
  //
  // `liveVal(row, bsId, field)` returns the current value of a counter:
  // while editing, it uses the in-flight value from `editData` so PTS
  // and percentages update live as the coach types. Outside edit mode
  // it falls back to the stored row value.
  const liveVal = (row, bsId, field) => {
    if (isEditing && editData[bsId] && editData[bsId][field] !== undefined && editData[bsId][field] !== '') {
      return parseInt(editData[bsId][field]) || 0;
    }
    return row[field] || 0;
  };

  // PTS = (FGM - 3PM) * 2 + 3PM * 3 + FTM. Derived on every render.
  const derivePts = (row, bsId) => {
    const fgm = liveVal(row, bsId, 'fgm');
    const tpm = liveVal(row, bsId, 'three_pm');
    const ftm = liveVal(row, bsId, 'ftm');
    return (fgm - tpm) * 2 + tpm * 3 + ftm;
  };

  // Percentage derived from makes/attempts. Returns null when 0
  // attempts so formatPct/getPctColor render "-" / neutral colour
  // consistently. One decimal place to match NBA box-score convention.
  const derivePct = (row, bsId, makesField, attemptsField) => {
    const att = liveVal(row, bsId, attemptsField);
    if (att <= 0) return null;
    const made = liveVal(row, bsId, makesField);
    return Math.round((made / att * 100) * 10) / 10;
  };

  // Four Factors use shared calcFourFactors from teamAnalysis.js

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: C.muted }} />
          <p className="text-[14px]" style={{ color: C.muted }}>Loading game summary...</p>
        </div>
      </div>
    );
  }

  if (!boxScores) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-[14px] mb-4" style={{ color: C.muted }}>Game not found</p>
          <Button onClick={() => navigate('/dashboard')} className="text-[16px] font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.navy, color: '#fff' }}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const { game, home_team_name, away_team_name } = boxScores;
  const homeFourFactors = calcFourFactors(boxScores.home.totals, boxScores.away.totals);
  const awayFourFactors = calcFourFactors(boxScores.away.totals, boxScores.home.totals);

  const analysis = generateTeamAnalysis(boxScores.home.totals, boxScores.away.totals, homeFourFactors, awayFourFactors);

  const editableFields = ['pts', 'fgm', 'fga', 'three_pm', 'three_pa', 'ftm', 'fta', 'oreb', 'dreb', 'ast', 'stl', 'blk', 'to', 'pf'];

  const renderEditCell = (bsId, field, value) => {
    if (!isEditing || !editData[bsId]) {
      return <span>{value}</span>;
    }
    const rawVal = editData[bsId]?.[field];
    const displayVal = rawVal !== undefined && rawVal !== null ? rawVal : value;
    return (
      <input
        key={`${bsId}-${field}`}
        type="number"
        min="0"
        className="w-16 h-7 text-center text-xs border border-blue-300 rounded bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        value={displayVal}
        onFocus={(e) => e.target.select()}
        onChange={(e) => handleEditChange(bsId, field, e.target.value)}
        data-testid={`edit-${bsId}-${field}`}
      />
    );
  };

  const renderBoxScoreTable = (teamName, data, side) => {
    const newTotalsId = `_new_totals_${side}`;
    return (
    <div className="overflow-hidden rounded-xl" style={{ border: `1.5px solid ${C.border}` }}>
      <div className="px-5 py-3" style={{ background: C.navy }}>
        <h3 className="font-bold text-[18px] uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{teamName}</h3>
      </div>
      <div className="overflow-x-auto bg-white pt-2">
        <table className="w-full text-[14px]">
          <thead>
            <tr style={{ background: '#FAFCFE', borderBottom: `1px solid #DDE5EC` }}>
              <th className="px-3 py-2.5 text-left font-bold text-[12px] uppercase tracking-[.06em] sticky left-0" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1', background: '#FAFCFE' }}>Player</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>PTS</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em] whitespace-nowrap" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>FGM-A</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>FG%</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em] whitespace-nowrap" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>3PM-A</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>3P%</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em] whitespace-nowrap" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>FTM-A</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>FT%</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>OREB</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>DREB</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>REB</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>AST</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>STL</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>BLK</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>TO</th>
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>PF</th>
              {/* Build 221: Player Performance Index. Sum of a scoring
                  index and a possession index, each computed as
                  player/team ratios in percentage points (see
                  /app/frontend/src/lib/ppi.js). Team totals row shows
                  '—' since every ratio collapses to 1.0 there. */}
              <th className="px-2 py-2.5 text-center font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }} data-testid={`ppi-header-${side}`}>PPI</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: '#F4F7FA' }}>
            {data.players.length === 0 ? (
              <tr>
                <td colSpan="17" className="px-3 py-4 text-center italic" style={{ color: '#8FA3B1' }}>
                  Individual stats not tracked for this team
                </td>
              </tr>
            ) : (
              data.players.map((p) => {
                const ed = editData[p.box_score_id];
                return (
                  <tr key={p.box_score_id} className={`hover:bg-slate-50 ${isEditing ? 'bg-blue-50/20' : ''}`}>
                    <td className="px-3 py-2 font-medium sticky left-0 bg-white">
                      <span className="text-blue-600 font-bold">#{p.jersey_number || '?'}</span>{' '}
                      {p.player_name}
                      {p.is_starter && <span className="ml-1 text-xs text-slate-400">*</span>}
                    </td>
                    <td className="px-2 py-2 text-center font-bold text-lg" data-testid={`pts-cell-${p.box_score_id}`}>
                      {/* PTS is always derived from FGM/3PM/FTM — never
                          read from a stored field. Updates live as the
                          coach types into the shot counters during edit. */}
                      {derivePts(p, p.box_score_id)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {isEditing && ed ? (
                        <span className="flex items-center justify-center gap-0.5">
                          {renderEditCell(p.box_score_id, "fgm", p.fgm)}
                          <span>-</span>
                          {renderEditCell(p.box_score_id, "fga", p.fga)}
                        </span>
                      ) : (
                        <span>{p.fgm}-{p.fga}</span>
                      )}
                    </td>
                    <td className={`px-2 py-2 text-center font-medium ${getPctColor(derivePct(p, p.box_score_id, 'fgm', 'fga'), liveVal(p, p.box_score_id, 'fga'), 'fg')}`}>{formatPct(derivePct(p, p.box_score_id, 'fgm', 'fga'), liveVal(p, p.box_score_id, 'fga'))}</td>
                    <td className="px-2 py-2 text-center">
                      {isEditing && ed ? (
                        <span className="flex items-center justify-center gap-0.5">
                          {renderEditCell(p.box_score_id, "three_pm", p.three_pm)}
                          <span>-</span>
                          {renderEditCell(p.box_score_id, "three_pa", p.three_pa)}
                        </span>
                      ) : (
                        <span>{p.three_pm}-{p.three_pa}</span>
                      )}
                    </td>
                    <td className={`px-2 py-2 text-center font-medium ${getPctColor(derivePct(p, p.box_score_id, 'three_pm', 'three_pa'), liveVal(p, p.box_score_id, 'three_pa'), '3pt')}`}>{formatPct(derivePct(p, p.box_score_id, 'three_pm', 'three_pa'), liveVal(p, p.box_score_id, 'three_pa'))}</td>
                    <td className="px-2 py-2 text-center">
                      {isEditing && ed ? (
                        <span className="flex items-center justify-center gap-0.5">
                          {renderEditCell(p.box_score_id, "ftm", p.ftm)}
                          <span>-</span>
                          {renderEditCell(p.box_score_id, "fta", p.fta)}
                        </span>
                      ) : (
                        <span>{p.ftm}-{p.fta}</span>
                      )}
                    </td>
                    <td className={`px-2 py-2 text-center font-medium ${getPctColor(derivePct(p, p.box_score_id, 'ftm', 'fta'), liveVal(p, p.box_score_id, 'fta'), 'ft')}`}>{formatPct(derivePct(p, p.box_score_id, 'ftm', 'fta'), liveVal(p, p.box_score_id, 'fta'))}</td>
                    <td className="px-2 py-2 text-center">{renderEditCell(p.box_score_id, "oreb", p.oreb)}</td>
                    <td className="px-2 py-2 text-center">{renderEditCell(p.box_score_id, "dreb", p.dreb)}</td>
                    <td className="px-2 py-2 text-center font-medium">
                      {isEditing && ed ? getEditNum(p.box_score_id, 'oreb') + getEditNum(p.box_score_id, 'dreb') : p.reb}
                    </td>
                    <td className="px-2 py-2 text-center">{renderEditCell(p.box_score_id, "ast", p.ast)}</td>
                    <td className="px-2 py-2 text-center">{renderEditCell(p.box_score_id, "stl", p.stl)}</td>
                    <td className="px-2 py-2 text-center">{renderEditCell(p.box_score_id, "blk", p.blk)}</td>
                    <td className="px-2 py-2 text-center">{renderEditCell(p.box_score_id, "to", p.to)}</td>
                    <td className="px-2 py-2 text-center">{renderEditCell(p.box_score_id, "pf", p.pf)}</td>
                    <td className="px-2 py-2 text-center font-bold tabular-nums" data-testid={`ppi-cell-${p.box_score_id}`}>
                      {/* PPI is derived live against the team-totals row
                          we already have in scope (`data.totals`). It's
                          a pure read off the same fields the percentage
                          cells use, so it updates instantly when those
                          fields are edited inline. */}
                      {formatPPI(computePPI(p, data.totals))}
                    </td>
                  </tr>
                );
              })
            )}
            {data.totals && (() => {
              const t = data.totals;
              const tId = t.box_score_id;
              const tEd = editData[tId];
              const totalsEditable = isEditing && tEd;
              return (
              <tr className={`bg-slate-100 font-bold border-t-2 border-slate-300 ${totalsEditable ? 'bg-blue-50' : ''}`}>
                <td className="px-3 py-2 sticky left-0 bg-slate-100">TEAM TOTALS</td>
                <td className="px-2 py-2 text-center text-lg" data-testid={`pts-cell-${tId}`}>
                  {/* TEAM TOTALS PTS — same derivation rule. Always live. */}
                  {derivePts(t, tId)}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? (
                    <span className="flex items-center justify-center gap-0.5">
                      {renderEditCell(tId, "fgm", t.fgm)}
                      <span>-</span>
                      {renderEditCell(tId, "fga", t.fga)}
                    </span>
                  ) : <span>{t.fgm}-{t.fga}</span>}
                </td>
                <td className={`px-2 py-2 text-center ${getPctColor(derivePct(t, tId, 'fgm', 'fga'), liveVal(t, tId, 'fga'), 'fg')}`}>{formatPct(derivePct(t, tId, 'fgm', 'fga'), liveVal(t, tId, 'fga'))}</td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? (
                    <span className="flex items-center justify-center gap-0.5">
                      {renderEditCell(tId, "three_pm", t.three_pm)}
                      <span>-</span>
                      {renderEditCell(tId, "three_pa", t.three_pa)}
                    </span>
                  ) : <span>{t.three_pm}-{t.three_pa}</span>}
                </td>
                <td className={`px-2 py-2 text-center ${getPctColor(derivePct(t, tId, 'three_pm', 'three_pa'), liveVal(t, tId, 'three_pa'), '3pt')}`}>{formatPct(derivePct(t, tId, 'three_pm', 'three_pa'), liveVal(t, tId, 'three_pa'))}</td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? (
                    <span className="flex items-center justify-center gap-0.5">
                      {renderEditCell(tId, "ftm", t.ftm)}
                      <span>-</span>
                      {renderEditCell(tId, "fta", t.fta)}
                    </span>
                  ) : <span>{t.ftm}-{t.fta}</span>}
                </td>
                <td className={`px-2 py-2 text-center ${getPctColor(derivePct(t, tId, 'ftm', 'fta'), liveVal(t, tId, 'fta'), 'ft')}`}>{formatPct(derivePct(t, tId, 'ftm', 'fta'), liveVal(t, tId, 'fta'))}</td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? renderEditCell(tId, "oreb", t.oreb) : t.oreb}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? renderEditCell(tId, "dreb", t.dreb) : t.dreb}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable && tEd ? getEditNum(tId, 'oreb') + getEditNum(tId, 'dreb') : t.reb}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? renderEditCell(tId, "ast", t.ast) : t.ast}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? renderEditCell(tId, "stl", t.stl) : t.stl}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? renderEditCell(tId, "blk", t.blk) : t.blk}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? renderEditCell(tId, "to", t.to) : t.to}
                </td>
                <td className="px-2 py-2 text-center">
                  {totalsEditable ? renderEditCell(tId, "pf", t.pf) : t.pf}
                </td>
                {/* PPI is meaningless on the team-totals row (player/team
                    ratios collapse to 1.0). Show '—' for consistency. */}
                <td className="px-2 py-2 text-center text-slate-400" data-testid={`ppi-cell-${tId}`}>—</td>
              </tr>
              );
            })()}
            {!data.totals && isEditing && editData[newTotalsId] && (() => {
              const tId = newTotalsId;
              const tEd = editData[tId];
              return (
              <tr className="bg-blue-50 font-bold border-t-2 border-slate-300">
                <td className="px-3 py-2 sticky left-0 bg-blue-50">TEAM TOTALS</td>
                <td className="px-2 py-2 text-center text-lg">{renderEditCell(tId, "pts", 0)}</td>
                <td className="px-2 py-2 text-center">
                  <span className="flex items-center justify-center gap-0.5">
                    {renderEditCell(tId, "fgm", 0)}
                    <span>-</span>
                    {renderEditCell(tId, "fga", 0)}
                  </span>
                </td>
                <td className="px-2 py-2 text-center text-slate-400">-</td>
                <td className="px-2 py-2 text-center">
                  <span className="flex items-center justify-center gap-0.5">
                    {renderEditCell(tId, "three_pm", 0)}
                    <span>-</span>
                    {renderEditCell(tId, "three_pa", 0)}
                  </span>
                </td>
                <td className="px-2 py-2 text-center text-slate-400">-</td>
                <td className="px-2 py-2 text-center">
                  <span className="flex items-center justify-center gap-0.5">
                    {renderEditCell(tId, "ftm", 0)}
                    <span>-</span>
                    {renderEditCell(tId, "fta", 0)}
                  </span>
                </td>
                <td className="px-2 py-2 text-center text-slate-400">-</td>
                <td className="px-2 py-2 text-center">{renderEditCell(tId, "oreb", 0)}</td>
                <td className="px-2 py-2 text-center">{renderEditCell(tId, "dreb", 0)}</td>
                <td className="px-2 py-2 text-center">{tEd ? getEditNum(tId, 'oreb') + getEditNum(tId, 'dreb') : 0}</td>
                <td className="px-2 py-2 text-center">{renderEditCell(tId, "ast", 0)}</td>
                <td className="px-2 py-2 text-center">{renderEditCell(tId, "stl", 0)}</td>
                <td className="px-2 py-2 text-center">{renderEditCell(tId, "blk", 0)}</td>
                <td className="px-2 py-2 text-center">{renderEditCell(tId, "to", 0)}</td>
                <td className="px-2 py-2 text-center">{renderEditCell(tId, "pf", 0)}</td>
                {/* PPI placeholder — totals row is always '—'. */}
                <td className="px-2 py-2 text-center text-slate-400">—</td>
              </tr>
              );
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
  };

  const renderFourFactorCard = (label, homeVal, awayVal, lowerIsBetter) => {
    const homeWins = lowerIsBetter ? homeVal < awayVal : homeVal > awayVal;
    const awayWins = lowerIsBetter ? awayVal < homeVal : awayVal > homeVal;
    return (
      <div className="bg-white rounded-[10px] p-4 text-center" style={{ border: `1.5px solid ${C.border}` }}>
        <p className="text-[14px] font-bold uppercase tracking-[.12em] mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>{label}</p>
        <div className="flex items-center justify-between">
          <span className="text-[18px] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: homeWins ? C.green : C.navy }}>
            {homeVal.toFixed(1)}%
          </span>
          <span className="text-xs px-1" style={{ color: C.muted }}>vs</span>
          <span className="text-[18px] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: awayWins ? C.green : C.navy }}>
            {awayVal.toFixed(1)}%
          </span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif" }}>
      {/* Score Header */}
      <div className="rounded-xl overflow-hidden mb-6" style={{ background: C.navy }}>
        <div className="px-5 py-5 md:py-6">
          {/* Top row: date + share/replay buttons. Build 221: two
              shareable links next to Add Replay — `/p/<id>` for a
              parent-facing read-only box score and `/c/<id>` for a
              coach view with a season-wide game filter. Both routes
              are public (no auth wall) and the buttons copy the URL
              to the clipboard for easy text/email sharing. */}
          <div className="flex items-center justify-between mb-4 gap-3">
            <div className="text-[16px] font-bold tracking-[.08em] uppercase shrink-0" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#FFFFFF' }}>
              {new Date(game.date_time).toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
              })}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => {
                  // Inside Capacitor (TestFlight / App Store) the page
                  // is served from `capacitor://localhost`, so
                  // `window.location.origin` produces an unusable
                  // `capacitor://localhost/p/<id>` link. Use the
                  // production host explicitly when running natively
                  // so the link a coach copies actually opens in a
                  // browser. On web we keep `window.location.origin`
                  // so preview / staging environments still link to
                  // their own host.
                  const origin = Capacitor.isNativePlatform()
                    ? 'https://smarterhoops.com'
                    : window.location.origin;
                  const url = `${origin}/p/${gameId}`;
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(url).then(
                      () => toast.success('Parent link copied'),
                      () => toast.error('Could not copy link'),
                    );
                  } else {
                    window.prompt('Copy this link:', url);
                  }
                }}
                className="inline-flex items-center justify-center h-9 px-3 rounded-full text-[12px] font-bold uppercase tracking-[.06em] transition-all hover:opacity-90 active:scale-[.97]"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#FFFFFF', color: C.navy, border: 'none' }}
                data-testid="share-parent-link"
              >
                <Share2 className="w-4 h-4 mr-1.5" />
                Parent
              </button>
              <button
                onClick={() => {
                  const origin = Capacitor.isNativePlatform()
                    ? 'https://smarterhoops.com'
                    : window.location.origin;
                  const url = `${origin}/c/${gameId}`;
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(url).then(
                      () => toast.success('Coach link copied'),
                      () => toast.error('Could not copy link'),
                    );
                  } else {
                    window.prompt('Copy this link:', url);
                  }
                }}
                className="inline-flex items-center justify-center h-9 px-3 rounded-full text-[12px] font-bold uppercase tracking-[.06em] transition-all hover:opacity-90 active:scale-[.97]"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#FFFFFF', color: C.navy, border: 'none' }}
                data-testid="share-coach-link"
              >
                <Share2 className="w-4 h-4 mr-1.5" />
                Coach
              </button>
              <button
                onClick={() => { setReplayUrlInput(replayUrl); setShowReplayModal(true); }}
                className="inline-flex items-center justify-center h-9 px-4 rounded-full text-[12px] font-bold uppercase tracking-[.06em] transition-all hover:opacity-90 active:scale-[.97]"
                style={replayUrl
                  ? { fontFamily: "'Barlow Condensed', sans-serif", background: C.orange, color: C.navy, border: 'none' }
                  : { fontFamily: "'Barlow Condensed', sans-serif", background: C.red, color: '#fff', border: 'none' }
                }
                data-testid="open-replay-modal"
              >
                <Video className="w-4 h-4 mr-1.5" />
                {replayUrl ? 'Replay' : 'Add Replay'}
              </button>
            </div>
          </div>

          {/* Score block */}
          <div className="flex items-center justify-center gap-6 md:gap-10">
            <div className="text-center min-w-[80px] md:min-w-[120px]">
              <p className="text-[16px] font-bold uppercase tracking-[.08em] leading-tight mb-1.5" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#FFFFFF' }}>{home_team_name}</p>
              <p className="text-4xl md:text-5xl font-bold tabular-nums leading-none text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{game.home_score}</p>
            </div>
            <div className="text-2xl md:text-3xl font-bold select-none" style={{ color: 'rgba(255,255,255,.2)' }}>–</div>
            <div className="text-center min-w-[80px] md:min-w-[120px]">
              <p className="text-[16px] font-bold uppercase tracking-[.08em] leading-tight mb-1.5" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#FFFFFF' }}>{away_team_name}</p>
              <p className="text-4xl md:text-5xl font-bold tabular-nums leading-none text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{game.away_score}</p>
            </div>
          </div>

          {/* Status badges */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {game.status === 'final' && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-[.08em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.green, color: '#fff' }}>
                <CheckCircle className="w-3 h-3 mr-1" /> Final
              </span>
            )}
            {game.status === 'locked' && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-[.08em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#64748b', color: '#fff' }}>
                <Lock className="w-3 h-3 mr-1" /> Locked
              </span>
            )}
            {boxScores.is_imported && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-[.08em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#8e44ad', color: '#fff' }} data-testid="imported-game-badge">
                <FileSpreadsheet className="w-3 h-3 mr-1" /> Imported
              </span>
            )}
            {boxScores.edit_count > 0 && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-[.08em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.blue, color: '#fff' }} data-testid="edited-game-badge">
                <Pencil className="w-3 h-3 mr-1" /> Edited ({boxScores.edit_count})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Previous / Next Game Navigation */}
      {(adjGames.prev || adjGames.next) && (
        <div className="flex items-center justify-between mb-6" data-testid="game-nav">
          {adjGames.prev ? (
            <button
              onClick={() => navigate(`/game/${adjGames.prev.game_id}/summary`)}
              className="flex items-center gap-2 px-4 h-10 rounded-lg border border-slate-200 bg-white text-sm font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50 transition-colors"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="prev-game-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Prior Game
            </button>
          ) : <div />}
          {adjGames.next ? (
            <button
              onClick={() => navigate(`/game/${adjGames.next.game_id}/summary`)}
              className="flex items-center gap-2 px-4 h-10 rounded-lg border border-slate-200 bg-white text-sm font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50 transition-colors"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="next-game-btn"
            >
              Next Game <ChevronRight className="w-4 h-4" />
            </button>
          ) : <div />}
        </div>
      )}

      {/* Content */}
      <Tabs defaultValue="box-score" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6 h-12 rounded-lg" style={{ background: '#fff', border: `1.5px solid ${C.border}` }}>
          <TabsTrigger value="box-score" className="font-bold uppercase text-[14px] tracking-[.06em] data-[state=active]:shadow-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Box Score</TabsTrigger>
          <TabsTrigger value="play-by-play" className="font-bold uppercase text-[14px] tracking-[.06em] data-[state=active]:shadow-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Play-by-Play</TabsTrigger>
        </TabsList>

          <TabsContent value="box-score" className="flex flex-col gap-6">
            {/* Edit Controls */}
            {game.status !== 'locked' && (
              <div className="order-0 flex items-center justify-end gap-2" data-testid="edit-controls">
                {isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={cancelEditing}
                      className="h-9 font-bold uppercase text-[14px] tracking-[.04em]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                      data-testid="cancel-edit-btn"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                    <Button
                      onClick={saveEdits}
                      disabled={savingEdits}
                      className="h-9 font-bold uppercase text-[14px] tracking-[.04em] text-white hover:opacity-90"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", backgroundColor: C.green }}
                      data-testid="save-edit-btn"
                    >
                      {savingEdits ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <button
                    onClick={startEditing}
                    className="inline-flex items-center justify-center h-9 px-4 rounded-full text-[12px] font-bold uppercase tracking-[.06em] transition-all hover:opacity-90 active:scale-[.97]"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#fff', color: C.blue, border: `1.5px solid ${C.blue}` }}
                    data-testid="start-edit-btn"
                  >
                    <Edit className="w-4 h-4 mr-1.5" /> Edit Box Score
                  </button>
                )}
              </div>
            )}

            {isEditing && (
              <div className="order-1 rounded-lg p-3 flex items-start gap-2" style={{ background: 'rgba(41,128,185,.06)', border: `1px solid rgba(41,128,185,.2)` }}>
                <Pencil className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: C.blue }} />
                <p className="text-[14px]" style={{ color: C.navy }}>
                  Editing mode active. Click on any stat value to modify it. Changes will be logged in the edit history.
                </p>
              </div>
            )}

            {/* Box Score Tables */}
            <div className="order-4">
              {renderBoxScoreTable(home_team_name, boxScores.home, "home")}
            </div>
            <div className="order-5">
              {renderBoxScoreTable(away_team_name, boxScores.away, "away")}
            </div>

            {/* Four Factors Section */}
            {homeFourFactors && awayFourFactors ? (
              <div className="order-2 overflow-hidden rounded-xl" style={{ border: `1.5px solid ${C.border}` }}>
                <div className="px-5 py-3" style={{ background: C.navy }}>
                  <h3 className="font-bold text-[22px] uppercase tracking-tight text-white text-center" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Advanced Stats</h3>
                  <div className="flex items-center justify-between mt-2 px-4">
                    <span className="text-[16px] font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: 'rgba(255,255,255,.6)' }}>{home_team_name}</span>
                    <span className="text-[16px] font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: 'rgba(255,255,255,.6)' }}>{away_team_name}</span>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3" style={{ background: '#FAFCFE' }}>
                  {renderFourFactorCard("eFG%", homeFourFactors.efg, awayFourFactors.efg)}
                  {renderFourFactorCard("TOV%", homeFourFactors.tov, awayFourFactors.tov, true)}
                  {renderFourFactorCard("ORB%", homeFourFactors.orb, awayFourFactors.orb)}
                  {renderFourFactorCard("FTR", homeFourFactors.ftr, awayFourFactors.ftr)}
                </div>
              </div>
            ) : (
              <div className="order-2 overflow-hidden rounded-xl" style={{ border: `1.5px solid ${C.border}` }} data-testid="four-factors-missing">
                <div className="px-5 py-3" style={{ background: C.navy }}>
                  <h3 className="font-bold text-[22px] uppercase tracking-tight text-white text-center" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Advanced Stats</h3>
                </div>
                <div className="p-6 bg-white text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: C.orange }} />
                  <p className="text-[14px] font-bold mb-2" style={{ color: C.navy }}>Advanced Stats cannot be calculated</p>
                  <p className="text-[14px] mb-4" style={{ color: C.muted }}>
                    {!boxScores.home.totals && !boxScores.away.totals
                      ? `We need team totals for both ${home_team_name} and ${away_team_name} to calculate Advanced Stats.`
                      : !boxScores.away.totals
                        ? `We need ${away_team_name}'s team totals to calculate Advanced Stats. Click below to enter their stats.`
                        : `We need ${home_team_name}'s team totals to calculate Advanced Stats. Click below to enter their stats.`
                    }
                  </p>
                  {game.status !== 'locked' && !isEditing && (
                    <Button
                      variant="outline"
                      onClick={startEditing}
                      className="h-9 font-bold uppercase text-[14px] tracking-[.04em]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", borderColor: C.blue, color: C.blue }}
                      data-testid="four-factors-edit-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" /> Add Missing Stats
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Team Comparison */}
            {boxScores.home.totals && boxScores.away.totals ? (
              <div className="order-3 overflow-hidden rounded-xl" style={{ border: `1.5px solid ${C.border}` }}>
                <div className="px-5 py-3" style={{ background: C.navy }}>
                  <h3 className="font-bold text-[22px] uppercase tracking-tight text-center text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Team Comparison</h3>
                </div>
                <div className="p-6 bg-white">
                  <div className="grid grid-cols-3 gap-4 mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
                    <div className="text-center">
                      <span className="text-[18px] font-bold" style={{ color: C.navy }}>{home_team_name}</span>
                    </div>
                    <div className="text-center self-center">
                      <span className="text-[14px] font-medium uppercase" style={{ color: C.muted }}>Stat</span>
                    </div>
                    <div className="text-center">
                      <span className="text-[18px] font-bold" style={{ color: C.navy }}>{away_team_name}</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {[
                      // Team Comparison shooting % cells derive from
                      // makes/attempts on render — no stored pct fields.
                      // makesKey lets us call derivePct uniformly.
                      { key: 'fg_pct',     makesKey: 'fgm',      attemptsKey: 'fga',      label: 'FG%',  derive: true },
                      { key: 'three_p_pct', makesKey: 'three_pm', attemptsKey: 'three_pa', label: '3PT%', derive: true },
                      { key: 'ft_pct',     makesKey: 'ftm',      attemptsKey: 'fta',      label: 'FT%',  derive: true },
                      { key: 'reb', label: 'REB', fmt: v => v ?? 0 },
                      { key: 'to', label: 'TO', fmt: v => v ?? 0, lower: true },
                      { key: 'pf', label: 'FOULS', fmt: v => v ?? 0, lower: true },
                    ].map(({ key, makesKey, attemptsKey, label, fmt, derive, lower }) => {
                      // For derived % rows, compute home/away from raw counts
                      // so the comparison is internally consistent with the
                      // per-player table above.
                      const hCount = derive
                        ? ((boxScores.home.totals[makesKey] || 0))
                        : (boxScores.home.totals[key] || 0);
                      const aCount = derive
                        ? ((boxScores.away.totals[makesKey] || 0))
                        : (boxScores.away.totals[key] || 0);
                      const hAttempts = attemptsKey ? boxScores.home.totals[attemptsKey] : undefined;
                      const aAttempts = attemptsKey ? boxScores.away.totals[attemptsKey] : undefined;
                      const hv = derive ? ((hAttempts > 0) ? (hCount / hAttempts * 100) : 0) : hCount;
                      const av = derive ? ((aAttempts > 0) ? (aCount / aAttempts * 100) : 0) : aCount;
                      const homeWins = lower ? hv < av : hv > av;
                      const awayWins = lower ? av < hv : av > hv;
                      const renderHome = derive
                        ? ((hAttempts > 0) ? `${(hCount / hAttempts * 100).toFixed(1)}%` : '-')
                        : fmt(boxScores.home.totals[key], hAttempts);
                      const renderAway = derive
                        ? ((aAttempts > 0) ? `${(aCount / aAttempts * 100).toFixed(1)}%` : '-')
                        : fmt(boxScores.away.totals[key], aAttempts);
                      return (
                        <div key={key} className="grid grid-cols-3 gap-4 items-center">
                          <div className="text-center text-[14px] font-semibold" style={{ color: homeWins ? C.green : C.navy }}>{renderHome}</div>
                          <div className="text-center text-[14px] font-medium" style={{ color: C.muted }}>{label}</div>
                          <div className="text-center text-[14px] font-semibold" style={{ color: awayWins ? C.green : C.navy }}>{renderAway}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="order-3 overflow-hidden rounded-xl" style={{ border: `1.5px solid ${C.border}` }} data-testid="team-comparison-missing">
                <div className="px-5 py-3" style={{ background: C.navy }}>
                  <h3 className="font-bold text-[22px] uppercase tracking-tight text-center text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Team Comparison</h3>
                </div>
                <div className="p-6 bg-white text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: C.orange }} />
                  <p className="text-[14px] font-bold mb-2" style={{ color: C.navy }}>Team Comparison cannot be calculated</p>
                  <p className="text-[14px] mb-4" style={{ color: C.muted }}>
                    {!boxScores.home.totals && !boxScores.away.totals
                      ? `We need team totals for both teams to show this comparison.`
                      : !boxScores.away.totals
                        ? `We need ${away_team_name}'s team totals to show this comparison. Click below to enter their stats.`
                        : `We need ${home_team_name}'s team totals to show this comparison. Click below to enter their stats.`
                    }
                  </p>
                  {game.status !== 'locked' && !isEditing && (
                    <Button
                      variant="outline"
                      onClick={startEditing}
                      className="h-9 font-bold uppercase text-[14px] tracking-[.04em]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", borderColor: C.blue, color: C.blue }}
                      data-testid="team-comparison-edit-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" /> Add Missing Stats
                    </Button>
                  )}
                </div>
              </div>
            )}
            {analysis && (
              <div className="order-1 flex justify-center" data-testid="analysis-button-container">
                <Button
                  onClick={() => setShowAnalysisModal(true)}
                  className="h-12 px-6 text-white font-bold uppercase text-[16px] tracking-[.06em] hover:opacity-90"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", backgroundColor: C.red }}
                  data-testid="open-analysis-modal"
                >
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Game Breakdown
                </Button>
              </div>
            )}

            {/* Export Buttons */}
            <div className="order-6 flex justify-end space-x-3">
              <Button variant="outline" onClick={exportBoxScoreCSV} className="text-[14px] font-bold uppercase tracking-[.04em]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {boxScores.home.totals && derivePts(boxScores.home.totals, boxScores.home.totals.box_score_id) !== game.home_score && (
              <div className="order-7 rounded-lg p-4 text-[14px]" style={{ background: 'rgba(245,166,35,.08)', border: `1px solid rgba(245,166,35,.3)`, color: '#b47100' }}>
                <strong>Warning:</strong> Calculated home team points ({derivePts(boxScores.home.totals, boxScores.home.totals.box_score_id)})
                don't match final score ({game.home_score}). Stats may be incomplete.
              </div>
            )}

            {/* Edit History Section */}
            <div className="order-8" data-testid="edit-history-section">
              <button
                onClick={toggleHistory}
                className="w-full flex items-center justify-between bg-white rounded-lg p-4 hover:bg-[#FAFCFE] transition-colors"
                style={{ border: `1.5px solid ${C.border}` }}
                data-testid="toggle-edit-history"
              >
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4" style={{ color: C.muted }} />
                  <span className="text-[14px] font-bold uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
                    Edit History {boxScores.edit_count > 0 ? `(${boxScores.edit_count})` : ''}
                  </span>
                </div>
                {showHistory ? <ChevronUp className="w-4 h-4" style={{ color: C.muted }} /> : <ChevronDown className="w-4 h-4" style={{ color: C.muted }} />}
              </button>

              {showHistory && (
                <div className="bg-white border border-t-0 border-slate-200 rounded-b-lg p-4">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : editHistory.length === 0 ? (
                    <p className="text-sm text-slate-500 italic text-center py-3" data-testid="no-edit-history">No edits have been made to this game.</p>
                  ) : (
                    <div className="space-y-4" data-testid="edit-history-list">
                      {editHistory.map((edit, idx) => (
                        <div key={edit.edit_id || idx} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                              {edit.editor_name}
                            </span>
                            <span className="text-xs text-slate-400">
                              {new Date(edit.created_at).toLocaleString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                                hour: 'numeric', minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {edit.changes?.map((change, cIdx) => (
                              <div key={cIdx} className="text-xs text-slate-600">
                                <span className="font-medium">#{change.jersey_number} {change.player_name}:</span>{' '}
                                {Object.entries(change.changes || {}).map(([field, vals], fIdx) => (
                                  <span key={fIdx}>
                                    {fIdx > 0 && ', '}
                                    <span className="uppercase font-medium">{field.replace('_', ' ')}</span>{' '}
                                    <span className="text-red-500">{vals.old}</span>{' '}
                                    <span className="text-slate-400">→</span>{' '}
                                    <span className="text-green-600">{vals.new}</span>
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="play-by-play">
            <div className="flex flex-wrap gap-4 mb-6">
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger className="w-[140px]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  <SelectValue placeholder="Period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Periods</SelectItem>
                  <SelectItem value="1">Q1</SelectItem>
                  <SelectItem value="2">Q2</SelectItem>
                  <SelectItem value="3">Q3</SelectItem>
                  <SelectItem value="4">Q4</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sideFilter} onValueChange={setSideFilter}>
                <SelectTrigger className="w-[140px]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  <SelectValue placeholder="Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  <SelectItem value="home">{home_team_name}</SelectItem>
                  <SelectItem value="away">{away_team_name}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={exportPlayByPlayCSV} className="text-[14px] font-bold uppercase tracking-[.04em]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            <div className="overflow-hidden rounded-xl" style={{ border: `1.5px solid ${C.border}` }}>
              <div className="overflow-x-auto bg-white">
                <table className="w-full text-[14px]">
                  <thead style={{ background: '#FAFCFE', borderBottom: `1px solid ${C.border}` }}>
                    <tr>
                      <th className="px-3 py-2.5 text-left font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>Period</th>
                      <th className="px-3 py-2.5 text-left font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>Time</th>
                      <th className="px-3 py-2.5 text-left font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>Team</th>
                      <th className="px-3 py-2.5 text-left font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>Player</th>
                      <th className="px-3 py-2.5 text-left font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>Action</th>
                      <th className="px-3 py-2.5 text-left font-bold text-[12px] uppercase tracking-[.06em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted }}>Result</th>
                      {game.status !== 'locked' && (
                        <th className="px-3 py-2 text-right font-bold text-xs uppercase text-slate-600">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredPlayByPlay.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-3 py-8 text-center text-slate-500">No events recorded</td>
                      </tr>
                    ) : (
                      filteredPlayByPlay.map((event) => (
                        <tr key={event.event_id} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-bold">Q{event.period}</td>
                          <td className="px-3 py-2">{event.game_clock_time}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              event.side === 'home' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {event.side === 'home' ? home_team_name : away_team_name}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {event.player_name ? (
                              <>
                                <span className="text-blue-600 font-bold">#{event.jersey_number || '?'}</span>{' '}
                                {event.player_name}
                              </>
                            ) : (
                              <span className="text-slate-400">Team</span>
                            )}
                          </td>
                          <td className="px-3 py-2 uppercase font-medium">
                            {event.event_type?.replace('_', ' ')}
                            {event.sub_type && ` (${event.sub_type})`}
                          </td>
                          <td className="px-3 py-2">
                            {event.points > 0 ? (
                              <span className="text-green-600 font-bold">+{event.points}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          {game.status !== 'locked' && (
                            <td className="px-3 py-2 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirm(event)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {game.status === 'locked' && (
              <div className="rounded-lg p-4 text-[14px] mt-4" style={{ background: '#FAFCFE', border: `1.5px solid ${C.border}`, color: C.muted }}>
                <Lock className="w-4 h-4 inline mr-2" />
                This game is locked. Contact support to make changes.
              </div>
            )}
          </TabsContent>
        </Tabs>

      {/* Replay URL Modal */}
      <Dialog open={showReplayModal} onOpenChange={setShowReplayModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-bold uppercase text-[18px] tracking-[.04em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
              <Video className="w-5 h-5" /> Game Replay
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {replayUrl && (
              <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: '#FAFCFE', border: `1px solid ${C.border}` }}>
                <a href={replayUrl} target="_blank" rel="noopener noreferrer"
                  className="text-[14px] font-medium flex items-center gap-1 truncate flex-1 hover:underline" style={{ color: C.blue }}
                  data-testid="replay-url-link">
                  Watch Replay <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
                <Button variant="ghost" size="sm" onClick={handleClearReplayUrl} disabled={savingReplayUrl}
                  className="h-7 px-2" style={{ color: C.red }} data-testid="clear-replay-url-btn">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-[14px] font-medium" style={{ color: C.navy }}>{replayUrl ? 'Update URL' : 'Add Replay URL'}</label>
              <Input type="url" placeholder="https://youtube.com/watch?v=..." value={replayUrlInput}
                onChange={(e) => setReplayUrlInput(e.target.value)} className="w-full" data-testid="replay-url-input" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowReplayModal(false)}>Cancel</Button>
              <Button onClick={handleSaveReplayUrl} disabled={savingReplayUrl}
                className="flex-1 text-white text-[16px] font-bold uppercase hover:opacity-90"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.navy }}
                data-testid="save-replay-url-btn">
                {savingReplayUrl ? <RefreshCw className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-[18px]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.red }}>Delete Event</DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <div className="py-4">
              <p className="text-[14px] mb-4" style={{ color: C.muted }}>
                Are you sure you want to delete this event? Box scores will be recalculated.
              </p>
              <div className="rounded-lg p-3 text-[14px] mb-4" style={{ background: '#FAFCFE', border: `1px solid ${C.border}` }}>
                <strong>Q{deleteConfirm.period}</strong> &middot; {deleteConfirm.player_name || 'Team'} &middot; {deleteConfirm.event_type?.replace('_', ' ').toUpperCase()}
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                <Button className="flex-1 text-white text-[16px] font-bold uppercase hover:opacity-90"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.red }}
                  onClick={() => handleDeleteEvent(deleteConfirm.event_id)}>
                  Delete Event
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Game Breakdown Modal */}
      <GameBreakdownModal
        open={showAnalysisModal}
        onOpenChange={setShowAnalysisModal}
        analysis={analysis}
        game={game}
      />
    </div>
  );
}
